import { useMemo, useState } from 'react';
import { AlertCircle, Loader2, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useTenantUsers } from '../../hooks/useTenantUsers';
import {
  useRequestSubscribers,
  useUpdateRequestSubscribers,
} from '../../hooks/useSupportRequests';
import type { SupportRequest } from '../../services/supportRequest.service';
import { showToast } from '@/services/toast.service';
import { apiErrorMessage } from '../../services/api.service';
import { isPermissionError } from '../../lib/queryRetry';

/**
 * Choose who gets notified about a request.
 *
 * Modelled on the artifact share dialog: a checkbox list over the tenant's
 * users, committed by an explicit Save. The difference is what "unchecked"
 * means — sharing controls who may *read* an artifact, while this controls who
 * receives *email*. Nothing here hides the request from anyone.
 *
 * The requester is deliberately not in the toggle list. Every notification
 * already excludes the person who caused it, so someone who unsubscribed the
 * requester would produce a request whose author hears nothing back — the exact
 * failure this whole subscriber concept exists to prevent. The artifact dialog
 * withholds the owner's own row for the same reason.
 */
export function RequestSubscribers({ request }: { request: SupportRequest }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={() => setOpen(true)}
        title="Manage subscribers"
        aria-label="Manage subscribers"
      >
        <Users className="h-4 w-4" />
      </Button>

      {/* Mounted only while open, so the draft selection reseeds from the
          server each time rather than surviving a cancelled edit. */}
      {open && (
        <SubscribersDialog request={request} onOpenChange={setOpen} />
      )}
    </>
  );
}

/**
 * Why an error gets its own component: the two ways this dialog can fail both
 * need to say *what the reader can do about it*, and "API Error" — which is
 * literally what `err.message` holds — says nothing. A refusal is a different
 * sentence from a failure, so the caller supplies both.
 */
function ErrorNote({
  error,
  permissionText,
  fallback,
}: {
  error: unknown;
  permissionText: string;
  fallback: string;
}) {
  const text = isPermissionError(error)
    ? permissionText
    : apiErrorMessage(error, fallback);

  return (
    <div className="flex gap-2 rounded-md border border-border bg-muted/40 px-3 py-2">
      <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  );
}

function SubscribersDialog({
  request,
  onOpenChange,
}: {
  request: SupportRequest;
  onOpenChange: (v: boolean) => void;
}) {
  const {
    data: tenantUsers,
    isLoading: usersLoading,
    error: usersError,
  } = useTenantUsers();
  const {
    data: subscribers,
    isLoading: subsLoading,
    error: subsError,
  } = useRequestSubscribers(request.id);
  const updateSubscribers = useUpdateRequestSubscribers();

  const subscribedIds = useMemo(
    () => (subscribers || []).map((s) => s.user_id),
    [subscribers],
  );

  // null until the server list has arrived, so an early render can't seed the
  // draft from an empty list and then save that as "unsubscribe everyone".
  const [draft, setDraft] = useState<string[] | null>(null);
  const selected = draft ?? subscribedIds;

  const repEmails = useMemo(
    () =>
      new Set(
        (subscribers || [])
          .filter((s) => s.is_account_rep)
          .map((s) => s.email.toLowerCase()),
      ),
    [subscribers],
  );

  const loading = usersLoading || subsLoading;

  // Two different failures, and they don't degrade the same way.
  //
  // The subscriber list failing leaves nothing to show — that's a dead end.
  // The *tenant user* list failing (it's Admin-only, so any non-admin gets a
  // 403 here) only costs us the set of people you could add: who is already
  // subscribed is still known, and worth showing. Silently rendering an empty
  // checkbox list would read as "nobody else is in this account", which is a
  // lie, and saving from it would look like it had unsubscribed everyone.
  const cannotLoadSubscribers = !!subsError;
  const cannotChoosePeople = !!usersError;
  const readOnly = cannotChoosePeople || cannotLoadSubscribers;

  // Who the requester is, by every identifier the two payloads might agree on.
  //
  // `request.created_by` alone was not enough: it is optional on the request
  // payload, so where it is absent the filter below matched nobody and the
  // requester appeared in the toggle list — checked, unsubscribable, and
  // directly contradicting the "always subscribed" row above it. The subscriber
  // list is the server's own verdict (`is_requester` is computed against
  // created_by in the database), so it is the one to trust; email is the last
  // resort, and safe because both lists resolve from the same Frontegg records.
  const requester = useMemo(
    () => (subscribers || []).find((s) => s.is_requester),
    [subscribers],
  );

  const requesterIds = useMemo(() => {
    const ids = new Set<string>();
    if (request.created_by) ids.add(request.created_by);
    if (requester) ids.add(requester.user_id);
    return ids;
  }, [request.created_by, requester]);

  const requesterEmail = (requester?.email || '').toLowerCase();

  // The requester is always subscribed and is not offered as a toggle.
  const selectable = (tenantUsers || []).filter(
    (u) =>
      !requesterIds.has(u.id) &&
      !(requesterEmail && (u.email || '').toLowerCase() === requesterEmail),
  );

  const requesterLabel =
    requester?.name ||
    request.created_by_label ||
    'The person who opened this request';

  const toggle = (userId: string) => {
    setDraft((prev) => {
      const base = prev ?? subscribedIds;
      return base.includes(userId)
        ? base.filter((id) => id !== userId)
        : [...base, userId];
    });
  };

  const add = selected.filter((id) => !subscribedIds.includes(id));
  const remove = subscribedIds.filter(
    (id) => !selected.includes(id) && !requesterIds.has(id),
  );
  const dirty = add.length > 0 || remove.length > 0;

  const save = async () => {
    try {
      await updateSubscribers.mutateAsync({ id: request.id, add, remove });
      showToast.success('Subscribers updated');
      onOpenChange(false);
    } catch (err: any) {
      showToast.error('Failed to update subscribers', {
        description: err?.message,
      });
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Manage subscribers</DialogTitle>
          <DialogDescription>
            Subscribers are emailed when someone posts, unless they're already
            reading the request in the app. Everyone in the account can still
            open it.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : cannotLoadSubscribers ? (
            <ErrorNote
              error={subsError}
              permissionText="You don't have permission to see who is subscribed to this request."
              fallback="Couldn't load the subscribers for this request."
            />
          ) : (
            <>
              <div className="rounded-md bg-muted/50 px-3 py-2">
                <p className="text-sm font-medium truncate">{requesterLabel}</p>
                <p className="text-xs text-muted-foreground">
                  Opened this request — always subscribed
                </p>
              </div>

              {cannotChoosePeople && (
                <ErrorNote
                  error={usersError}
                  permissionText="You don't have permission to change who is subscribed — only an account admin can. Here's who is subscribed today."
                  fallback="Couldn't load the people in this account, so subscribers can't be changed right now. Here's who is subscribed today."
                />
              )}

              <div className="space-y-1 max-h-64 overflow-auto">
                {cannotChoosePeople ? (
                  (subscribers || [])
                    .filter((sub) => !sub.is_requester)
                    .map((sub) => (
                      <div key={sub.user_id} className="rounded-md px-2 py-1.5">
                        <p className="text-sm truncate">{sub.name}</p>
                        {sub.email && sub.email !== sub.name && (
                          <p className="text-xs text-muted-foreground truncate">
                            {sub.email}
                          </p>
                        )}
                      </div>
                    ))
                ) : selectable.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No other users in this account.
                  </p>
                ) : (
                  selectable.map((u) => (
                    <label
                      key={u.id}
                      className="flex items-center gap-3 rounded-md p-2 hover:bg-accent cursor-pointer"
                    >
                      <Checkbox
                        checked={selected.includes(u.id)}
                        onCheckedChange={() => toggle(u.id)}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">
                          {u.name || u.email}
                        </p>
                        {u.name && (
                          <p className="text-xs text-muted-foreground truncate">
                            {u.email}
                          </p>
                        )}
                      </div>
                      {repEmails.has((u.email || '').toLowerCase()) && (
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground flex-shrink-0">
                          Pliable
                        </span>
                      )}
                    </label>
                  ))
                )}
              </div>
            </>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
          <Button
            onClick={save}
            disabled={!dirty || loading || readOnly || updateSubscribers.isPending}
          >
            {updateSubscribers.isPending && (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            )}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
